/**
 * Phase 8 control helpers shared by the layout engine and the renderer
 * (docs/ops-protocol.md, "Phase 8: interaction", Controls). Pure and DOM-free:
 * slider value snapping and the US-English formatting of a `datepicker` in
 * its time zone.
 */

// Slider
// ---------------------------------------------------------------------------

/** Decimal places of `step` (0.25 → 2), capped so float noise never leaks into the JSON. */
function decimalsOf(step: number): number {
  const text = String(step);
  const exp = /e-(\d+)$/.exec(text);
  if (exp) return Math.min(10, Number(exp[1]));
  const dot = text.indexOf('.');
  return dot < 0 ? 0 : Math.min(10, text.length - dot - 1);
}

/**
 * The slider value for a thumb fraction (0 at `min`, 1 at `max`): snapped to
 * the nearest multiple of `step` from `min` (when `step` is positive) and
 * clamped to `min…max`. A degenerate range (`max <= min`) yields `min`.
 */
export function snapSliderValue(fraction: number, min: number, max: number, step: number | null): number {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return Number.isFinite(min) ? min : 0;
  const f = Number.isFinite(fraction) ? Math.min(1, Math.max(0, fraction)) : 0;
  let value = min + f * (max - min);
  if (step !== null && Number.isFinite(step) && step > 0) {
    value = min + Math.round((value - min) / step) * step;
    value = Number(value.toFixed(decimalsOf(step)));
  } else {
    value = Math.round(value * 1e6) / 1e6;
  }
  return Math.min(max, Math.max(min, value));
}

/** The thumb's position for `value`: 0 at `min`, 1 at `max`, clamped. */
export function sliderFraction(value: number, min: number, max: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(min) || !Number.isFinite(max) || max <= min) return 0;
  return Math.min(1, Math.max(0, (value - min) / (max - min)));
}

// Date picker
// ---------------------------------------------------------------------------

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** The wall clock of an instant in a zone. */
interface WallClock {
  year: number;
  month: number; // 0-based
  day: number;
  hours: number;
  minutes: number;
}

/** The viewer's IANA zone, or UTC when the runtime cannot say. */
function localZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

const formatters = new Map<string, Intl.DateTimeFormat>();

/** A formatter giving an instant's wall clock in `zone`; an unknown zone reads as UTC. */
function formatterFor(zone: string): Intl.DateTimeFormat {
  let format = formatters.get(zone);
  if (!format) {
    const options: Intl.DateTimeFormatOptions = { hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' };
    try {
      format = new Intl.DateTimeFormat('en-US', { ...options, timeZone: zone });
    } catch {
      format = new Intl.DateTimeFormat('en-US', { ...options, timeZone: 'UTC' });
    }
    formatters.set(zone, format);
  }
  return format;
}

/** The zone a picker shows and edits in: the one it names, else the viewer's. */
function zoneOf(timeZone: string | undefined): string {
  return timeZone || localZone();
}

/** The wall clock of `seconds` (since 1970) in `zone`. */
export function wallClock(seconds: number, timeZone?: string): WallClock {
  const ms = (Number.isFinite(seconds) ? seconds : 0) * 1000;
  const parts: Record<string, number> = {};
  for (const part of formatterFor(zoneOf(timeZone)).formatToParts(new Date(ms))) {
    if (part.type !== 'literal') parts[part.type] = Number(part.value);
  }
  // `h23` still reads "24" for midnight in some engines.
  return { year: parts['year'] ?? 1970, month: (parts['month'] ?? 1) - 1, day: parts['day'] ?? 1, hours: (parts['hour'] ?? 0) % 24, minutes: parts['minute'] ?? 0 };
}

/** The instant (seconds since 1970) at a wall clock in `zone`; in a gap (a clock skipped forward) the later reading, in an overlap the earlier. */
export function instantOf(clock: WallClock, timeZone?: string): number {
  const asUTC = (c: WallClock): number => Date.UTC(c.year, c.month, c.day, c.hours, c.minutes, 0) / 1000;
  const wanted = asUTC(clock);
  // The zone's offset at a nearby instant, then checked at the answer itself (a DST change may sit between).
  let guess = wanted - (asUTC(wallClock(wanted, timeZone)) - wanted);
  const again = wanted - (asUTC(wallClock(guess, timeZone)) - guess);
  if (again !== guess && asUTC(wallClock(again, timeZone)) === wanted) guess = again;
  return guess;
}

/** "Oct 3, 2026": the date of `seconds` in `timeZone` (the viewer's when absent), in the US English abbreviated form. */
export function formatPickerDate(seconds: number, timeZone?: string): string {
  const c = wallClock(seconds, timeZone);
  return `${MONTHS[c.month]} ${c.day}, ${c.year}`;
}

/** "7:00 AM": the time of `seconds` in `timeZone`, in the US English 12-hour form. */
export function formatPickerTime(seconds: number, timeZone?: string): string {
  const c = wallClock(seconds, timeZone);
  const period = c.hours < 12 ? 'AM' : 'PM';
  const hour12 = c.hours % 12 === 0 ? 12 : c.hours % 12;
  return `${hour12}:${pad2(c.minutes)} ${period}`;
}

/** `yyyy-mm-dd` for a native `<input type="date">`, in `timeZone`. */
export function dateInputValue(seconds: number, timeZone?: string): string {
  const c = wallClock(seconds, timeZone);
  return `${c.year}-${pad2(c.month + 1)}-${pad2(c.day)}`;
}

/** `HH:MM` for a native `<input type="time">`, in `timeZone`. */
export function timeInputValue(seconds: number, timeZone?: string): string {
  const c = wallClock(seconds, timeZone);
  return `${pad2(c.hours)}:${pad2(c.minutes)}`;
}

/**
 * Seconds after a native input changed, read in `timeZone`: the `date`
 * (`yyyy-mm-dd`) replaces the day and keeps the time of day of `seconds`;
 * the `time` (`HH:MM`) replaces the time of day and keeps the day. Seconds
 * within the minute are dropped, like the pickers' minute granularity. An
 * unparsable value leaves `seconds` as it was.
 */
export function combineDateSeconds(seconds: number, change: { date?: string; time?: string }, timeZone?: string): number {
  const clock = wallClock(seconds, timeZone);
  if (change.date !== undefined) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(change.date);
    if (!m) return seconds;
    clock.year = Number(m[1]);
    clock.month = Number(m[2]) - 1;
    clock.day = Number(m[3]);
  }
  if (change.time !== undefined) {
    const m = /^(\d{2}):(\d{2})/.exec(change.time);
    if (!m) return seconds;
    clock.hours = Number(m[1]);
    clock.minutes = Number(m[2]);
  }
  return instantOf(clock, timeZone);
}

/** True when two instants fall on the same day in `timeZone` (a time input's `min`/`max` only apply then). */
export function sameDay(a: number, b: number, timeZone?: string): boolean {
  return dateInputValue(a, timeZone) === dateInputValue(b, timeZone);
}
